import { supabase } from './supabaseClient';
import type { SearchResults } from '../types';

// Escapar caracteres especiais do ilike para evitar injection
function sanitizeSearchTerm(term: string): string {
  return term.replace(/[%_\\]/g, '\\$&');
}

export const searchService = {
  /**
   * Busca global nos projetos do usuário
   * @param {string} query - Termo de busca
   * @param {string[]} userProjectIds - IDs dos projetos do usuário (filtro de segurança)
   */
  async searchGlobal(query: string, userProjectIds: string[] = []): Promise<SearchResults> {
    if (!query || query.trim().length < 2 || !userProjectIds.length) {
      return { tasks: [], risks: [], changes: [], costs: [], pendencias: [] };
    }

    const searchTerm: string = sanitizeSearchTerm(query.trim().toLowerCase());

    try {
      // Buscar em paralelo em todas as tabelas, filtrado por projetos do usuário
      const [tasksResult, risksResult, changesResult, costsResult, pendenciasResult] =
        await Promise.all([
          // Tarefas - busca em name e description
          supabase
            .from('tasks')
            .select('id, project_id, name, type, description, progress, projects(id, name)')
            .in('project_id', userProjectIds)
            .or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
            .limit(20),

          // Riscos
          supabase
            .from('risks')
            .select('id, project_id, title, description, status, probability, impact, projects(id, name)')
            .in('project_id', userProjectIds)
            .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
            .limit(10),

          // Mudanças
          supabase
            .from('changes')
            .select('id, project_id, title, description, status, type, projects(id, name)')
            .in('project_id', userProjectIds)
            .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
            .limit(10),

          // Custos
          supabase
            .from('costs')
            .select('id, project_id, description, category, planned_amount, actual_amount, projects(id, name)')
            .in('project_id', userProjectIds)
            .or(`description.ilike.%${searchTerm}%,category.ilike.%${searchTerm}%`)
            .limit(10),

          // Pendências
          supabase
            .from('pendencias')
            .select('id, project_id, demanda, area, responsavel, status, criticidade, projects(id, name)')
            .in('project_id', userProjectIds)
            .or(`demanda.ilike.%${searchTerm}%,area.ilike.%${searchTerm}%,responsavel.ilike.%${searchTerm}%`)
            .limit(10)
        ]);

      // Função para mapear resultados com informações do projeto
      const mapWithProjectInfo = <T extends Record<string, unknown>>(items: T[] | null): (T & { projectId: string; projectName: string })[] => {
        if (!items || items.length === 0) return [];
        return items.map(item => ({
          ...item,
          projectId: item.project_id as string,
          projectName: ((item.projects as Record<string, unknown>)?.name as string) || 'Projeto'
        }));
      };

      return {
        tasks: mapWithProjectInfo(tasksResult.data),
        risks: mapWithProjectInfo(risksResult.data),
        changes: mapWithProjectInfo(changesResult.data),
        costs: mapWithProjectInfo(costsResult.data),
        pendencias: mapWithProjectInfo(pendenciasResult.data)
      } as unknown as SearchResults;
    } catch (error) {
      console.error('Erro na busca global:', error);
      return { tasks: [], risks: [], changes: [], costs: [], pendencias: [] };
    }
  }
};
